import {
  endOfMonth,
  endOfWeek,
  isThisMonth,
  isThisWeek,
  isToday,
  isWithinInterval,
  isYesterday,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import HttpCodes from 'http-status-codes';
import { PREMIUM_AI_AGENTS } from '@/administration/administration.constants';
import { agents, getDataLabAiAgents } from '@/aiAssistant/aiAssistant.types';
import { markRunningThinkingSections } from '@/aiAssistant/streaming.utilities';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { isSystemTest } from '@/core/utilities';
import { ITEM_TYPES } from '@/homescreen/homescreen.constants';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { APPSERVER_API_CONTENT_TYPE } from '@/main/app.constants';
import { isDataLabAiAssistant } from '@/main/routing.utilities';
import {
  aiAssistantEnabled,
  genAIAgents,
  genAIEnabled,
  getAgentQCustomPrompts,
} from '@/services/systemConfiguration.utilities';
import { getCsrfToken } from '@/utilities/auth.utilities';
import { generateRequestId, getOriginLabel, getOriginUrl } from '@/utilities/http.utilities';
import { logError } from '@/utilities/logger';
import { errorToast } from '@/utilities/toast.utilities';
import { isMobileDevice } from '@/utilities/utilities';
import { getAxiosInstance, isAxiosError } from '@seeqdev/api';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { SeeqNames } from '@seeqdev/constants';
import type {
  AIMemory,
  Agent,
  AnswerSubmissionPayload,
  AnswerSubmissionResponse,
  AssistantOrigin,
  ChatData,
  ChatMessage,
  DocumentItem,
  GroupedHistoryEntryType,
  HistoryEntryType,
  MemoriesResponse,
  MigrateUserChatsResponse,
  SelectedDocument,
  ThinkingSectionItem,
  TokenUsage,
  WorksheetsResponse,
} from '@/aiAssistant/aiAssistant.types';
import type { MarkdownRenderingLocation } from '@/markdown/markdown.types';
// Import the Seeq SDK components needed for the functions
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';
import type { WorksheetOutputV1 } from '@seeqdev/api/model/WorksheetOutputV1';

export function headers(origin?: MarkdownRenderingLocation | AssistantOrigin) {
  return {
    [SeeqNames.API.Headers.Csrf]: getCsrfToken(),
    [SeeqNames.API.Headers.RequestOrigin]: origin || SeeqNames.Origins.Chat,
    [SeeqNames.API.Headers.RequestOriginURL]: getOriginUrl(),
    [SeeqNames.API.Headers.RequestOriginLabel]: getOriginLabel(),
    [SeeqNames.API.Headers.RequestId]: generateRequestId(),
  };
}

/**
 * Gets the Chats for the current user.
 *
 * @returns  A chronologically ordered dictionary of chat conversations
 */
export async function getChats(): Promise<Record<string, HistoryEntryType>> {
  const response = await fetch('/genai/chats', { method: 'GET', headers: headers() });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get chats: ${response.status} - ${errorText}`);
  }
  return await response.json();
}

/**
 * Gets the messages for a specific chat ID.
 *
 * @param chatId The ID of the chat to fetch messages for
 * @returns The chat data including messages and metadata
 */
export async function getChatMessages(chatId: string): Promise<ChatData> {
  const response = await fetch(`/genai/chats/${chatId}`, { method: 'GET', headers: headers() });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get chat messages: ${response.status} - ${errorText}`);
  }
  return (await response.json()) as ChatData;
}

export async function deleteChat(chatId: string): Promise<void> {
  await fetch(`/genai/chats?chat_id=${chatId}`, { method: 'DELETE', headers: headers() });
}

/**
 * Submits a batch of ask_user answers for the given chat.
 * Resolves pending agent-side ask_user tool calls so the LLM can resume generating.
 */
export async function submitAskUserAnswers(
  chatId: string,
  answers: AnswerSubmissionPayload[],
): Promise<AnswerSubmissionResponse> {
  try {
    const response = await getAxiosInstance().post<AnswerSubmissionResponse>(
      `/genai/llm/chat/${encodeURIComponent(chatId)}/answer`,
      { answers },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const status = error.response?.status ?? 'unknown';
      const body =
        typeof error.response?.data === 'string' ? error.response.data : JSON.stringify(error.response?.data ?? '');
      throw new Error(`Failed to submit ask_user answers (${status}): ${body}`);
    }
    throw error;
  }
}

/**
 * Migrates all chats from one user to another.
 * Used when deleting users to transfer their chat history to a new owner.
 *
 * @param fromUserId The user ID to migrate chats from
 * @param toUserId The user ID to migrate chats to
 * @param removeOrphans Whether to remove any orphaned chats after migration (default: true)
 * @returns Promise that resolves when migration is complete
 */
export async function migrateUserChats(fromUserId: string, toUserId: string, removeOrphans = true): Promise<void> {
  try {
    const response = await fetch('/genai/admin/chats/migrate', {
      method: 'POST',
      headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
      body: JSON.stringify({
        from_identity: fromUserId,
        to_identity: toUserId,
        remove_orphans: removeOrphans,
      }),
    });

    let responseBody: MigrateUserChatsResponse | string;
    try {
      responseBody = await response.json();
    } catch (error) {
      responseBody = await response.text();
    }

    if (!response.ok || typeof responseBody === 'string') {
      logError(`Failed to migrate chats from user ${fromUserId} to ${toUserId}. Response: ${responseBody}`);
      throw new Error(`Chat migration failed: ${responseBody}`);
    }

    if (response.status === HttpCodes.MULTI_STATUS && responseBody.failed > 0) {
      logError(
        `Partial chat migration from user ${fromUserId} to ${toUserId}. Number of failed items: ${responseBody.failed}`,
      );
      throw new Error(`Partial chat migration. Number of failed chats: ${responseBody.failed}`);
    }
  } catch (error) {
    logError(`Error migrating chats from user ${fromUserId} to ${toUserId}:`, String(error));
    throw error;
  }
}

function getCapableAgents(): string {
  const agents = genAIAgents();
  if (!hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Agent_Q)) {
    return agents
      .split(',')
      .map((agent: string) => agent.trim())
      .filter(
        (agent: string) =>
          !PREMIUM_AI_AGENTS.some((premiumAgent) => premiumAgent.toLowerCase() === agent.toLowerCase()),
      )
      .join(',');
  }
  return agents;
}

export function getFriendlyMarkup(id: string) {
  const element = document.getElementById(id);
  if (!element) return '';

  const range = document.createRange();
  range.selectNodeContents(element);
  const selection = window.getSelection();
  if (!selection) return '';

  selection.removeAllRanges();
  selection.addRange(range);
  const text = selection.toString();
  selection.removeAllRanges();
  return text;
}

export function getAgents(): Agent[] {
  if (isDataLabAiAssistant()) {
    const availableAgents = getDataLabAiAgents();
    return agents.filter((agent) => availableAgents?.includes(agent.key));
  }
  const availableAgents = getCapableAgents()?.toLowerCase();
  return agents.filter((agent) => availableAgents?.includes(agent.key));
}

export function getAgentsForNewChat(): Agent[] {
  if (isDataLabAiAssistant()) {
    const availableAgents = getDataLabAiAgents();
    return agents.filter((agent) => availableAgents?.includes(agent.key));
  }
  const availableAgents = getCapableAgents()?.toLowerCase();
  return agents.filter((agent) => agent.key !== 'code' && availableAgents?.includes(agent.key));
}

export const parseAgentQCustomPrompts = (rawPrompts: string): string[] => {
  if (!rawPrompts) return [];

  try {
    const parsed = JSON.parse(rawPrompts);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((prompt: unknown) => typeof prompt === 'string' && prompt.trim().length > 0);
  } catch {
    return [];
  }
};

export const getParsedAgentQCustomPrompts = (): string[] => parseAgentQCustomPrompts(getAgentQCustomPrompts());

export function getDefaultDisplayed(): boolean {
  return !isSystemTest() && !isMobileDevice() && aiAssistantEnabled() && genAIEnabled();
}

/**
 * Returns the `tokenUsage` from the most recent bot message that has one, or `undefined`
 * if no bot message in the list carries usage information.
 */
export function getLatestTokenUsage(messages: ChatMessage[]): TokenUsage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === 'bot' && message.tokenUsage) return message.tokenUsage;
  }
  return undefined;
}

export const updateChat = async (chatId: string, { favorite, name }: { favorite?: boolean; name?: string }) => {
  return await fetch(`/genai/chats/${chatId}`, {
    method: 'PUT',
    headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
    body: JSON.stringify({
      favorite: favorite === undefined ? null : favorite,
      name: name === undefined ? null : name,
    }),
  });
};

export const groupChats = (chats: Record<string, HistoryEntryType>): GroupedHistoryEntryType => {
  const today = new Date();
  const groupedChats: GroupedHistoryEntryType = {
    favorites: [],
    shared_with_me: [],
    today: [],
    yesterday: [],
    this_week: [],
    last_week: [],
    this_month: [],
    last_month: [],
    older: [],
  };

  const determineCategory = (
    updatedAt: string | undefined | null,
    favorite: boolean,
    shared: boolean,
  ): keyof GroupedHistoryEntryType => {
    if (favorite) return 'favorites';
    if (shared) return 'shared_with_me';
    if (!updatedAt) return 'older';

    const updatedAtDate = new Date(updatedAt);
    if (isNaN(updatedAtDate.getTime())) return 'older';

    if (isToday(updatedAtDate)) return 'today';
    if (isYesterday(updatedAtDate)) return 'yesterday';
    if (isThisWeek(updatedAtDate, { weekStartsOn: 1 })) return 'this_week';

    const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    if (isWithinInterval(updatedAtDate, { start: lastWeekStart, end: lastWeekEnd })) return 'last_week';

    if (isThisMonth(updatedAtDate)) return 'this_month';

    const lastMonthStart = startOfMonth(subDays(today, 30));
    const lastMonthEnd = endOfMonth(subDays(today, 30));
    if (isWithinInterval(updatedAtDate, { start: lastMonthStart, end: lastMonthEnd })) return 'last_month';

    return 'older';
  };

  Object.values(chats).forEach((chat) => {
    const category = determineCategory(chat.updatedAt, chat.favorite, !!chat.sharedBy);

    groupedChats[category].push(chat);
  });

  return groupedChats;
};

/**
 * Gets a specific workbook by ID.
 *
 * @param workbookId The ID of the workbook to retrieve
 * @returns The workbook details or null if not found/error
 */
export async function getWorkbookById(workbookId: string): Promise<DocumentItem | null> {
  try {
    const { data } = await sqWorkbooksApi.getWorkbook({ id: workbookId });

    // Convert the workbook response to match DocumentItem format
    return {
      id: data.id,
      name: data.name,
      type: data.type,
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || '',
      isPinned: false,
      owner: convertToOwner(data.owner),
      effectivePermissions: convertToPermissions(data.effectivePermissions),
      isArchived: data.isArchived || false,
    };
  } catch (error) {
    logError(`Error in getWorkbookById for ${workbookId}:`, String(error));
    return null;
  }
}

/**
 * Gets worksheets for a specific workbook/document.
 *
 * @param documentId The ID of the document to retrieve worksheets for
 * @param limit The maximum number of worksheets to return (default: 40)
 * @returns The worksheets or an empty response if not found/error
 */
export async function getWorksheets(documentId: string, limit = 40): Promise<WorksheetsResponse> {
  try {
    const { data } = await sqWorkbooksApi.getWorksheets({
      workbookId: documentId,
      offset: 0,
      limit,
      isArchived: false,
    });

    // Return SDK response in expected WorksheetsResponse format
    return {
      worksheets: data.worksheets || [],
      offset: data.offset || 0,
      limit: data.limit || 40,
    };
  } catch (error) {
    logError(`Error in getWorksheets for ${documentId}:`, String(error));
    // Return an empty response rather than throwing to prevent UI breakage
    return { worksheets: [], offset: 0, limit: 40 };
  }
}

/**
 * Extracts workbook and worksheet IDs from a URL.
 * Supports formats like:
 * - http://localhost:34216/workbook/WORKBOOK_ID/worksheet/WORKSHEET_ID
 * - http://localhost:34216/workbook/WORKBOOK_ID
 * - /workbook/WORKBOOK_ID/worksheet/WORKSHEET_ID
 * - /workbook/WORKBOOK_ID
 *
 * @param url The URL to parse
 * @returns Object containing extracted IDs, or null if no valid IDs found
 */
export function extractIdsFromUrl(url: string): { workbookId: string; worksheetId?: string } | null {
  try {
    if (!url) return null;

    // Define regex patterns for different URL formats
    // UUID format: 8-4-4-4-12 hexadecimal digits, sometimes with hyphens
    const uuidPattern = '[0-9A-F]{8}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{12}';

    // Pattern for full URL with both workbook and worksheet
    const fullPattern = new RegExp(`\\/workbook\\/(${uuidPattern})\\/worksheet\\/(${uuidPattern})`, 'i');
    const fullMatch = url.match(fullPattern);

    if (fullMatch && fullMatch.length >= 3) {
      return {
        workbookId: fullMatch[1],
        worksheetId: fullMatch[2],
      };
    }

    // Pattern for just a workbook ID
    const workbookPattern = new RegExp(`\\/workbook\\/(${uuidPattern})(?:\\/|$)`, 'i');
    const workbookMatch = url.match(workbookPattern);

    if (workbookMatch && workbookMatch.length >= 2) {
      return {
        workbookId: workbookMatch[1],
      };
    }

    return null;
  } catch (error) {
    logError('Error parsing URL:', String(error));
    return null;
  }
}

/**
 * Fetches a worksheet by its ID.
 *
 * @param workbookId The ID of the workbook containing the worksheet
 * @param worksheetId The ID of the worksheet to retrieve
 * @returns The worksheet details or null if not found/error
 */
export async function getWorksheetById(workbookId: string, worksheetId: string): Promise<WorksheetOutputV1 | null> {
  try {
    const { data } = await sqWorkbooksApi.getWorksheet({
      workbookId,
      worksheetId,
    });

    return data;
  } catch (error) {
    logError(`Error in getWorksheetById for ${worksheetId}:`, String(error));
    return null;
  }
}

/**
 * Helper function to convert SDK IdentityPreviewV1 to the expected owner format
 */
function convertToOwner(owner?: IdentityPreviewV1): {
  id: string;
  name: string;
  type: string;
  username: string;
  email: string;
} {
  return {
    id: owner?.id || '',
    name: owner?.name || '',
    type: owner?.type || '',
    username: owner?.username || '',
    email: owner?.email || '',
  };
}

/**
 * Helper function to convert SDK PermissionsV1 to the expected permissions format
 */
export function convertToPermissions(permissions?: PermissionsV1): { read: boolean; write: boolean; manage: boolean } {
  return {
    read: permissions?.read || false,
    write: permissions?.write || false,
    manage: permissions?.manage || false,
  };
}

/**
 * Normalizes selectedDocument to an array format.
 * This helper eliminates the repeated pattern of checking if selectedDocument is an array.
 *
 * @param selectedDocument - The selected document(s) which can be null, a single document, or an array
 * @returns An array of selected documents (empty array if input is null)
 */
export function normalizeSelectedDocuments(
  selectedDocument: SelectedDocument | SelectedDocument[] | null,
): SelectedDocument[] {
  if (!selectedDocument) {
    return [];
  }

  if (Array.isArray(selectedDocument)) {
    return selectedDocument;
  }

  return [selectedDocument];
}

export function getSelectedDocumentKey(document: SelectedDocument): string {
  const contentId = document.workbookId || document.projectId || document.folderId || document.parentId || document.id;
  return [document.type, contentId, document.worksheetId || ''].join(':');
}

export function getSelectedDocumentUrl(document: SelectedDocument): string {
  if (document.seeqUrl) return document.seeqUrl;
  if (document.projectId) return `/data-lab/${document.projectId}`;
  if (document.folderId) return `/folder/${document.folderId}`;
  if (document.workbookId && document.worksheetId) {
    return `/workbook/${document.workbookId}/worksheet/${document.worksheetId}`;
  }
  if (document.workbookId) return `/workbook/${document.workbookId}`;
  return '';
}

export function getSelectedDocumentDisplayName(document: SelectedDocument): string {
  return document.worksheetName || document.name || document.title || document.id;
}

export function getSelectedDocumentDisplayTitle(document: SelectedDocument): string {
  const workbookName = document.name || document.title;
  if (document.worksheetId && document.worksheetName && workbookName) {
    return `${workbookName} / ${document.worksheetName}`;
  }

  return getSelectedDocumentDisplayName(document);
}

/**
 * Gets the appropriate icon class for a document type.
 *
 * @param type - The document type
 * @returns The CSS class string for the document type icon
 */
export function getDocumentTypeIcon(type: string): string {
  switch (type) {
    case ITEM_TYPES.ANALYSIS:
      return 'fc fc-analysis';
    case ITEM_TYPES.ACTION_BOARD:
      return 'fa fa-sparkles';
    case ITEM_TYPES.TOPIC:
      return 'fc fc-report';
    case ITEM_TYPES.PROJECT:
      return 'fc fc-seeq-datalab';
    case ITEM_TYPES.FOLDER:
      return 'fa-folder-open';
    case ITEM_TYPES.JOURNAL:
      return 'fa-book';
    case ITEM_TYPES.REPORT:
      return 'fa-book';
    case ITEM_TYPES.VANTAGE:
      return 'fc-vantage';
    default:
      return 'fa-file-alt';
  }
}

/**
 * Gets the appropriate CSS class for a document type.
 *
 * @param type - The document type
 * @returns The CSS class string for the document type styling
 */
export function getDocumentTypeClass(type: string): string {
  const baseClasses = 'workbenchDisplay';

  switch (type) {
    case ITEM_TYPES.ANALYSIS:
      return baseClasses;
    case ITEM_TYPES.JOURNAL:
      return `${baseClasses} workbenchAnalysis`;
    case ITEM_TYPES.REPORT:
      return `${baseClasses} workbenchReport`;
    case ITEM_TYPES.TOPIC:
      return `${baseClasses} workbenchTopic`;
    case ITEM_TYPES.PROJECT:
      return `${baseClasses} workbenchProject`;
    case ITEM_TYPES.VANTAGE:
      return `${baseClasses} workbenchVantage`;
    case ITEM_TYPES.FOLDER:
      return `${baseClasses} sq-text-primary`;
    default:
      return baseClasses;
  }
}

/**
 * Gets the worksheet icon class based on the parent document type.
 *
 * @param parentDocumentType - The type of the parent document
 * @returns The CSS class string for the worksheet icon
 */
export function getWorksheetIconClass(parentDocumentType: string): string {
  return getDocumentTypeClass(parentDocumentType);
}

/**
 * Determines if the input text looks like a URL containing workbook/worksheet IDs.
 *
 * @param text - The text to check
 * @returns True if the text appears to be a workbook URL
 */
export function isWorkbookUrl(text: string): boolean {
  // Check if the text has the URL pattern we're looking for
  return (
    text.includes('/workbook/') &&
    /[0-9A-F]{8}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{12}/i.test(text)
  );
}

/**
 * Remove a leading back-tick, single-quote, or double-quote that sits
 * **immediately in front of a high-risk shell command** (wget, curl, bash,
 * …). Doing so prevents Cloudflare's managed WAF rule 932100/932150
 * (“Unix Command Injection” in the OWASP Core Rule Set) from flagging the
 * request and returning **Error 1020 – Access denied**.
 *
 * References
 *   • Cloudflare doc – Error 1020:
 * https://developers.cloudflare.com/support/troubleshooting/http-status-codes/cloudflare-1xxx-errors/#error-1020-access-denied
 *   • OWASP CRS rule file – REQUEST-932-APPLICATION-ATTACK-RCE.conf:
 *     https://github.com/coreruleset/coreruleset/blob/v3.3/dev/rules/REQUEST-932-APPLICATION-ATTACK-RCE.conf
 */
export const stripQuotedCommand = (text: string) => {
  const cmds = [
    'wget',
    'curl',
    'bash',
    'sh',
    'python',
    'perl',
    'php',
    'nc',
    'netcat',
    'scp',
    'ftp',
    'tftp',
    'awk',
    'sed',
    'cat',
    'head',
    'tail',
    'tr',
    'chmod',
    'rm',
    'mv',
  ].join('|');

  const re = new RegExp(`([\\\`'"]|(?:\\\`{2,})|(?:'{2,})|(?:"{2,}))(\\s*)((?:${cmds})\\b[\\s\\S]*?)(?:\\1|$)`, 'gi');
  return text.replace(re, (_m, _q, ws, body) => ws + body);
};

/**
 * Calculate section end time as the sum of all item durations plus section start time
 */
export function calculateSectionEndTime(items: ThinkingSectionItem[], startTime: number): number {
  if (items.length === 0) {
    return Date.now();
  }

  // Sum the duration of all items that have both start and end times
  const totalItemDuration = items
    .filter((item) => item.startTime && item.endTime)
    .reduce((sum, item) => sum + (item.endTime! - item.startTime!), 0);

  return startTime + totalItemDuration;
}

/**
 * Fetches all user memories from the backend (both approved and pending)
 */
export async function getAllMemories(): Promise<MemoriesResponse> {
  try {
    const response = await fetch('/genai/user_memories', {
      method: 'GET',
      headers: headers(),
    });
    const memoriesData = (await response.json()) as MemoriesResponse;
    return memoriesData;
  } catch (error) {
    logError('Error fetching user memories:', String(error));
    return { approved: [], pending: [] };
  }
}

/**
 * Helper function to fetch memories by type (approved or pending)
 */
async function getMemoriesByType(type: 'approved' | 'pending'): Promise<AIMemory[]> {
  try {
    const memories = await getAllMemories();
    return memories[type];
  } catch (error) {
    logError(`Error fetching ${type} memories:`, String(error));
    return [];
  }
}

/**
 * Fetches only approved user memories (backward compatibility)
 */
export async function getUserMemories(): Promise<AIMemory[]> {
  return getMemoriesByType('approved');
}

/**
 * Fetches only pending user memories
 */
export async function getPendingMemories(): Promise<AIMemory[]> {
  return getMemoriesByType('pending');
}

/**
 * Deletes a specific approved user memory (for backward compatibility)
 */
export async function deleteUserMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/approved/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });

    if (!response.ok) {
      // For HTTP errors, use the generic delete error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.DISCARD_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error deleting approved user memory:', String(error));
    return false;
  }
}

/**
 * Updates a specific user memory
 */
export async function updateUserMemory(memoryId: string, title: string, content: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}`, {
      method: 'PUT',
      headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
      body: JSON.stringify({
        title,
        content,
      }),
    });

    if (!response.ok) {
      // Handle 406 Not Acceptable errors for inappropriate content
      if (response.status === 406) {
        errorToast({
          messageKey: 'AI_ASSISTANT.ERROR.INAPPROPRIATE_CONTENT',
        });
        return false;
      }

      // Handle 507 Insufficient Storage errors with specific toast messages
      if (response.status === HttpCodes.INSUFFICIENT_STORAGE) {
        try {
          const errorData = await response.json();
          // The backend returns { detail: { detail: "message", error: "ERROR_CODE" } }
          const errorCode = errorData.detail?.error;
          if (errorCode === 'MEMORY_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.MEMORY_LIMIT_EXCEEDED',
            });
          } else if (errorCode === 'TOKEN_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.TOKEN_LIMIT_EXCEEDED',
            });
          } else {
            // Fallback for other 507 errors
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
            });
          }
        } catch (parseError) {
          // If we can't parse the JSON, fall back to generic error
          errorToast({
            messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
          });
        }
        return false;
      }

      // For other HTTP errors, use the generic error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error updating user memory:', String(error));
    errorToast({
      messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
    });
    return false;
  }
}

/**
 * Reverts a memory update by restoring the previous content
 */
export async function revertMemoryUpdate(
  memoryId: string,
  previousContent: string,
  previousTitle: string,
): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}`, {
      method: 'PUT',
      headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
      body: JSON.stringify({
        title: previousTitle,
        content: previousContent,
      }),
    });

    return response.ok;
  } catch (error) {
    logError('Error reverting memory update:', String(error));
    return false;
  }
}

/**
 * Approves a pending memory, moving it to the approved list
 */
export async function approvePendingMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}/approve`, {
      method: 'POST',
      headers: headers(),
    });

    if (!response.ok) {
      // Handle 507 Insufficient Storage errors with specific toast messages
      if (response.status === HttpCodes.INSUFFICIENT_STORAGE) {
        try {
          const errorData = await response.json();
          // The backend returns { detail: { detail: "message", error: "ERROR_CODE" } }
          const errorCode = errorData.detail?.error;
          if (errorCode === 'MEMORY_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.MEMORY_LIMIT_EXCEEDED',
            });
          } else if (errorCode === 'TOKEN_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.TOKEN_LIMIT_EXCEEDED',
            });
          } else {
            // Fallback for other 507 errors
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
            });
          }
        } catch (parseError) {
          // If we can't parse the JSON, fall back to generic error
          errorToast({
            messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
          });
        }
        return false;
      }

      // For other HTTP errors, use the generic error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error approving pending memory:', String(error));

    return false;
  }
}

/**
 * Deletes a pending memory
 */
export async function deletePendingMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/pending/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });
    if (!response.ok) {
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.DISCARD_MEMORY',
      });
      return false;
    }
    return response.ok;
  } catch (error) {
    logError('Error deleting pending memory:', String(error));
    return false;
  }
}

/**
 * Deletes an approved memory
 */
export async function deleteApprovedMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/approved/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });

    return response.ok;
  } catch (error) {
    logError('Error deleting approved memory:', String(error));
    return false;
  }
}

// Memory event system for cross-component communication
export type MemoryEvent = {
  type: 'approved' | 'rejected' | 'deleted';
  memoryId: string;
  timestamp: number;
};

class MemoryEventDispatcher {
  private listeners: ((event: MemoryEvent) => void)[] = [];

  subscribe(listener: (event: MemoryEvent) => void) {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  dispatch(event: MemoryEvent) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        logError('Error in memory event listener:', String(error));
      }
    });
  }

  // Convenience methods
  notifyApproved(memoryId: string) {
    this.dispatch({ type: 'approved', memoryId, timestamp: Date.now() });
  }

  notifyRejected(memoryId: string) {
    this.dispatch({ type: 'rejected', memoryId, timestamp: Date.now() });
  }

  notifyDeleted(memoryId: string) {
    this.dispatch({ type: 'deleted', memoryId, timestamp: Date.now() });
  }
}

// Global instance
export const memoryEventDispatcher = new MemoryEventDispatcher();

/**
 * Process messages when they are loaded from chat history.
 * This ensures thinking sections are properly processed for display and
 * leftover running rows restore as canceled or error instead of looking complete.
 */
export const processHistoryMessages = (messages: ChatMessage[]) => {
  return messages.map((message) => {
    if (message.role === 'bot' && message.thinkingSections && message.thinkingSections.length > 0) {
      const processedMessage = { ...message };
      const unfinishedStatus = message.terminalStatus === 'canceled' ? 'canceled' : 'error';
      processedMessage.thinkingSections = markRunningThinkingSections(message.thinkingSections, unfinishedStatus);
      return processedMessage;
    }
    return message;
  });
};
